// sales-page/src/pages/ThankYouBadge.jsx
import React, { useEffect } from 'react';
import salesApi from '../core/salesApi';
import salesLifetimeCss from './SalesLifetime.css?raw';

const ThankYouBadge = () => {
  useEffect(() => {
    // Clear email verification from localStorage after successful purchase
    salesApi.clearEmailVerification();
  }, []);

  // Page-scoped CSS: same design system as the main sales page, injected on
  // mount and removed on unmount so it never leaks onto other routes.
  useEffect(() => {
    const styleEl = document.createElement('style');
    styleEl.setAttribute('data-thankyou-badge', 'true');
    styleEl.textContent = salesLifetimeCss;
    document.head.appendChild(styleEl);
    return () => styleEl.remove();
  }, []);

  const unlocks = [
    { icon: '\u{1F4B0}', title: '50% Commission Forever', body: 'Earn 50% on every person you refer to TrafikTrak. No caps, no limits, no expiration. This is lifetime passive income.' },
    { icon: '⭐', title: 'Official Partner Badge', body: 'You’ll get an official "Funding Partner" badge on your profile and in the public directory (opt-in).' },
    { icon: '\u{1F5F3}️', title: 'Product Roadmap Votes', body: 'Vote on new features, integrations, and product direction. Your voice directly shapes TrafikTrak’s future.' },
    { icon: '\u{1F465}', title: 'Private Community', body: 'Access to the private Funding Partners community where you can network, share strategies, and collaborate.' },
    { icon: '\u{1F3C6}', title: 'Founding Cohort Status', body: 'You’re in the first 50. Forever. This status will never expand, giving you permanent early positioning.' },
    { icon: '\u{1F3AF}', title: 'VIP Support Access', body: 'Get priority support and direct access to the founder for any questions or feature requests.' }
  ];

  const nextSteps = [
    {
      n: 1,
      title: 'Check Your Email (within 5-10 minutes)',
      body: "You'll receive an email containing:",
      items: ['Your payment receipt', 'Access code for the Partners Desk (partners.trafiktrak.com)', 'Instructions to set up your partner account', 'Link to the private partner community']
    },
    {
      n: 2,
      title: 'Sign Up at the Partners Desk',
      body: 'Visit partners.trafiktrak.com and create your partner account using your access code. This is where you’ll get your unique referral link and track your commissions.',
      items: []
    },
    {
      n: 3,
      title: 'Get Your Referral Link',
      body: 'Once your partner account is set up, you’ll receive a unique tracking link like:',
      code: 'https://prelaunch.trafiktrak.com?promo=YOUR_CODE',
      footer: 'Every person who buys through your link = 50% commission in your pocket',
      items: []
    },
    {
      n: 4,
      title: 'Start Promoting',
      body: 'Share your link with your audience, network, or clients. We’ve created promotional materials to help you get started.',
      items: []
    }
  ];

  // TrafikTrak Lifetime plan is $58; founding partners earn 50% ($29) per
  // referral -- keep this in sync with the plan price on SalesPage.jsx.
  const commissionTier = { plan: 'TrafikTrak Lifetime Plan Referrals', price: 58, cut: 29, five: '145.00', ten: '290.00', twentyfive: '725.00' };

  return (
    <div className="min-h-screen font-['Arial',_sans-serif]">
      <div className="sales-page-container" style={{ padding: '48px 16px 64px' }}>

        {/* Hero */}
        <div style={{ textAlign: 'center', marginBottom: '28px' }}>
          <div style={{ fontSize: '48px', marginBottom: '10px' }}>&#127894;</div>
          <h1 className="sales-main-headline" style={{ marginBottom: '10px' }}>
            Welcome, Founding Partner! &#127881;
          </h1>
          <p style={{ fontSize: '19px', fontWeight: 700, color: '#111111', marginBottom: '14px' }}>
            You're now part of an exclusive group of 50 founding partners
          </p>
          <div className="doc-blue-banner" style={{ display: 'inline-block', margin: 0, borderRadius: '4px', padding: '10px 20px', fontSize: '16px' }}>
            Your partnership badge is active &middot; Check your email for next steps
          </div>
        </div>

        {/* Check your email banner */}
        <div className="doc-boxed-section" style={{ background: '#FFF9E6', borderColor: '#D97706', textAlign: 'center' }}>
          <p style={{ fontSize: '20px', fontWeight: 900, color: '#111111', margin: '0 0 8px' }}>&#128235; Check Your Email Now</p>
          <p style={{ fontSize: '16px', color: '#434343', margin: 0, lineHeight: 1.55 }}>
            Your TrafikTrak login credentials, payment receipt, and Partners Desk access code are on their way to your inbox. Check spam if you don't see it within 10 minutes.
          </p>
        </div>

        {/* What You Get */}
        <div className="sales-section-heading" style={{ marginTop: '44px' }}>Here's What You Just Unlocked:</div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" style={{ marginTop: '20px' }}>
          {unlocks.map((u) => (
            <div key={u.title} className="doc-boxed-section" style={{ margin: 0 }}>
              <div style={{ fontSize: '30px', marginBottom: '10px' }}>{u.icon}</div>
              <h3 style={{ fontSize: '17px', fontWeight: 900, color: '#111111', marginBottom: '8px' }}>{u.title}</h3>
              <p style={{ fontSize: '14px', color: '#434343', lineHeight: 1.55, margin: 0 }}>{u.body}</p>
            </div>
          ))}
        </div>

        {/* Next Steps */}
        <div className="sales-section-heading" style={{ marginTop: '44px' }}>What Happens Next?</div>

        <div style={{ marginTop: '20px' }}>
          {nextSteps.map((step) => (
            <div key={step.n} className="doc-boxed-section">
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
                <div style={{ width: '34px', height: '34px', border: '1.5px solid #111111', color: '#111111', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', fontWeight: 800, flexShrink: 0 }}>
                  {step.n}
                </div>
                <div style={{ flex: 1 }}>
                  <h3 style={{ fontSize: '19px', fontWeight: 900, color: '#111111', marginBottom: '8px' }}>{step.title}</h3>
                  <p style={{ fontSize: '15px', color: '#434343', lineHeight: 1.55, marginBottom: step.items.length || step.code ? '10px' : 0 }}>{step.body}</p>
                  {step.items.length > 0 && (
                    <div>
                      {step.items.map((item) => (
                        <div key={item} className="checklist-item" style={{ fontSize: '15px' }}>
                          <span className="check-icon">&#9989;</span>
                          <span>{item}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {step.code && (
                    <>
                      <div style={{ background: '#111111', color: '#4ADE80', fontFamily: 'monospace', fontSize: '14px', padding: '12px 16px', borderRadius: '4px' }}>
                        {step.code}
                      </div>
                      <p style={{ fontSize: '14px', color: '#666666', marginTop: '10px', marginBottom: 0 }}>{step.footer}</p>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Commission Calculator */}
        <div className="sales-section-heading" style={{ marginTop: '44px' }}>Quick Commission Math</div>

        <div className="doc-boxed-section" style={{ maxWidth: '360px', margin: '20px auto 0', textAlign: 'center' }}>
          <h3 style={{ fontSize: '16px', fontWeight: 900, color: '#111111', marginBottom: '12px' }}>{commissionTier.plan}</h3>
          <p style={{ fontSize: '14px', color: '#666666', margin: '0 0 4px' }}>Price: ${commissionTier.price}</p>
          <p style={{ fontSize: '24px', fontWeight: 900, color: '#1D4ED8', margin: '0 0 2px' }}>Your Cut: ${commissionTier.cut.toFixed(2)}</p>
          <p style={{ fontSize: '12px', color: '#999999', marginBottom: '14px' }}>per sale (50%)</p>
          <div style={{ fontSize: '13px', textAlign: 'left' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #E5E7EB', padding: '6px 0' }}>
              <span>5 referrals =</span><span style={{ fontWeight: 800 }}>${commissionTier.five}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #E5E7EB', padding: '6px 0' }}>
              <span>10 referrals =</span><span style={{ fontWeight: 800 }}>${commissionTier.ten}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0' }}>
              <span>25 referrals =</span><span style={{ fontWeight: 800, color: '#1D4ED8' }}>${commissionTier.twentyfive}</span>
            </div>
          </div>
        </div>

        <div className="doc-boxed-section" style={{ background: '#FFF9E6', borderColor: '#D97706', textAlign: 'center', marginTop: '20px' }}>
          <p style={{ fontSize: '18px', fontWeight: 800, color: '#111111', margin: '0 0 6px' }}>
            7 referrals at $29 each =
          </p>
          <p style={{ fontSize: '36px', fontWeight: 900, color: '#1D4ED8', margin: '0 0 6px' }}>$203.00 earned</p>
          <p style={{ fontSize: '14px', color: '#666666', margin: 0 }}>That's your $197 investment paid back, with room to spare. This is not a projection &mdash; it's simple 50% math.</p>
        </div>

        {/* Support */}
        <div className="sales-section-heading" style={{ marginTop: '44px' }}>Questions? We're Here To Help</div>
        <p style={{ textAlign: 'center', fontSize: '16px', color: '#434343', marginBottom: '20px' }}>
          As a Founding Partner, you get priority support access
        </p>

        <div className="grid md:grid-cols-2 gap-5">
          <div className="doc-boxed-section" style={{ margin: 0, textAlign: 'center' }}>
            <h3 style={{ fontSize: '17px', fontWeight: 900, color: '#111111', marginBottom: '8px' }}>&#128231; Email Partner Support</h3>
            <a href="mailto:partners@trafiktrak.com" style={{ color: '#1D4ED8', fontWeight: 700, textDecoration: 'underline' }}>
              partners@trafiktrak.com
            </a>
          </div>
          <div className="doc-boxed-section" style={{ margin: 0, textAlign: 'center' }}>
            <h3 style={{ fontSize: '17px', fontWeight: 900, color: '#111111', marginBottom: '8px' }}>&#128101; Join Partner Community</h3>
            <a href="https://community.trafiktrak.com/partners" style={{ color: '#1D4ED8', fontWeight: 700, textDecoration: 'underline' }}>
              community.trafiktrak.com/partners
            </a>
          </div>
        </div>

        {/* Footer */}
        <div style={{ marginTop: '48px', textAlign: 'center' }}>
          <p style={{ fontSize: '16px', color: '#434343', marginBottom: '12px' }}>
            Welcome to the founding cohort. Let's build something great together.
          </p>
          <a
            href="https://trafiktrak.com"
            style={{ color: '#1D4ED8', fontWeight: 700, fontSize: '16px', textDecoration: 'none' }}
          >
            &larr; Visit TrafikTrak
          </a>
        </div>
      </div>
    </div>
  );
};

export default ThankYouBadge;
